import {
  Box, Card, CardContent, Typography, TextField, Button, Grid, MenuItem,
  Divider, Switch, FormControlLabel, Alert, Tabs, Tab, Avatar, Chip,
  CircularProgress, Paper,
} from '@mui/material';
import { Save, Business, Palette, Language, Notifications, Receipt } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toggleDarkMode, setLanguage } from '../ui/uiSlice';
import { showSnackbar } from '../ui/uiSlice';
import api from '../../api/axios';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector(s => s.auth);
  const { darkMode, language } = useSelector(s => s.ui);
  const [tab, setTab] = useState(0);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [saving, setSaving] = useState(false);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: '', email: '', phone: '', city: '', address: '',
      currency: 'PKR', timezone: 'Asia/Karachi', tax_number: '',
      invoice_prefix: 'INV', receipt_footer: '', business_type: 'general',
      receipt_settings: {
        tagline: '',
        show_phone: true,
        show_address: true,
        show_ntn: true,
        show_cashier: true,
        show_loyalty: true,
        footer_line2: '',
        font_size: 'medium',
      },
    },
  });
  const watchedReceipt = watch('receipt_settings') || {};
  const watchedName = watch('name') || '';
  const watchedPhone = watch('phone') || '';
  const watchedAddress = watch('address') || '';
  const watchedFooter = watch('receipt_footer') || '';

  useEffect(() => {
    setLoadingSettings(true);
    api.get('/settings/business').then(r => {
      if (r.data.data) reset(r.data.data);
    }).catch(() => {}).finally(() => setLoadingSettings(false));
  }, [reset]);

  const onSave = async (data) => {
    setSaving(true);
    try {
      await api.put('/settings/business', data);
      dispatch(showSnackbar({ message: 'Business settings saved', severity: 'success' }));
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Save failed', severity: 'error' }));
    } finally { setSaving(false); }
  };

  const handleLang = (newLang) => {
    i18n.changeLanguage(newLang);
    dispatch(setLanguage(newLang));
    dispatch(showSnackbar({ message: `Language changed to ${newLang === 'en' ? 'English' : 'اردو'}`, severity: 'success' }));
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>{t('nav.settings')}</Typography>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab icon={<Business />} iconPosition="start" label="Business" />
          <Tab icon={<Receipt />} iconPosition="start" label="Receipt" />
          <Tab icon={<Palette />} iconPosition="start" label="Appearance" />
          <Tab icon={<Language />} iconPosition="start" label="Language" />
          <Tab icon={<Notifications />} iconPosition="start" label="Notifications" />
        </Tabs>

        <CardContent>
          {/* Business Settings */}
          <TabPanel value={tab} index={0}>
            {loadingSettings ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
              <Box component="form" onSubmit={handleSubmit(onSave)}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 24 }}>
                        {user?.name?.[0]?.toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography fontWeight={600}>{user?.name}</Typography>
                        <Chip label={user?.role} size="small" color="primary" sx={{ textTransform: 'capitalize' }} />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Business Name" {...register('name', { required: true })} error={!!errors.name} /></Grid>
                  <Grid item xs={12} md={6}><TextField fullWidth label="Email" {...register('email')} /></Grid>
                  <Grid item xs={6}><TextField fullWidth label="Phone" {...register('phone')} /></Grid>
                  <Grid item xs={6}><TextField fullWidth label="City" {...register('city')} /></Grid>
                  <Grid item xs={12}><TextField fullWidth label="Address" {...register('address')} /></Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth select label="Business Type" defaultValue="general" {...register('business_type')}>
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="clothing">Clothing / Garments</MenuItem>
                      <MenuItem value="medical">Medical / Pharmacy</MenuItem>
                      <MenuItem value="karyana">Karyana / Grocery</MenuItem>
                      <MenuItem value="cosmetics">Cosmetics</MenuItem>
                      <MenuItem value="mobile">Mobile / Electronics</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth select label="Currency" defaultValue="PKR" {...register('currency')}>
                      <MenuItem value="PKR">PKR — Pakistani Rupee</MenuItem>
                      <MenuItem value="USD">USD — US Dollar</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth select label="Timezone" defaultValue="Asia/Karachi" {...register('timezone')}>
                      <MenuItem value="Asia/Karachi">Asia/Karachi (PKT, UTC+5)</MenuItem>
                      <MenuItem value="UTC">UTC</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6}><TextField fullWidth label="Tax Number (NTN/STRN)" {...register('tax_number')} /></Grid>

                  <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">Invoice & Receipt</Typography></Divider></Grid>

                  <Grid item xs={4}><TextField fullWidth label="Invoice Prefix" {...register('invoice_prefix')} placeholder="INV" helperText="e.g. INV, SAL, POS" /></Grid>
                  <Grid item xs={8}><TextField fullWidth label="Receipt Footer Text" {...register('receipt_footer')} placeholder="Thank you for your business! | شکریہ" /></Grid>

                  <Grid item xs={12}>
                    <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} disabled={saving}>
                      {saving ? 'Saving…' : 'Save Settings'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </TabPanel>

          {/* Receipt Customization */}
          <TabPanel value={tab} index={1}>
            <Box component="form" onSubmit={handleSubmit(onSave)}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <Typography variant="subtitle2" fontWeight={700} mb={2}>Receipt Options</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Tagline / Slogan" {...register('receipt_settings.tagline')}
                        placeholder="e.g. Quality you can trust" helperText="Shown below business name on receipt" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Footer Line 1" {...register('receipt_footer')}
                        placeholder="Thank you for your business!" />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Footer Line 2" {...register('receipt_settings.footer_line2')}
                        placeholder="شکریہ — آپ کا دوبارہ خیرمقدم ہے" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth select label="Receipt Font Size" {...register('receipt_settings.font_size')}>
                        <MenuItem value="small">Small (9px)</MenuItem>
                        <MenuItem value="medium">Medium (11px)</MenuItem>
                        <MenuItem value="large">Large (13px)</MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">Show / Hide on Receipt</Typography></Divider>

                  <Grid container spacing={1}>
                    {[
                      ['show_phone',   'Phone Number'],
                      ['show_address', 'Address'],
                      ['show_ntn',     'NTN / Tax Number'],
                      ['show_cashier', 'Cashier Name'],
                      ['show_loyalty', 'Loyalty Points Balance'],
                    ].map(([field, label]) => (
                      <Grid item xs={12} sm={6} key={field}>
                        <FormControlLabel
                          control={<Switch {...register(`receipt_settings.${field}`)} defaultChecked />}
                          label={label}
                          sx={{ justifyContent: 'space-between', ml: 0, flexDirection: 'row-reverse', width: '100%' }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                {/* Live Preview */}
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>Live Preview — 80mm Thermal</Typography>
                  <Paper variant="outlined" sx={{ p: 2, fontFamily: 'monospace', fontSize: watchedReceipt.font_size === 'small' ? 9 : watchedReceipt.font_size === 'large' ? 13 : 11, maxWidth: 280, bgcolor: '#fff', color: '#000' }}>
                    <Box sx={{ textAlign: 'center', mb: 1 }}>
                      <Typography sx={{ fontFamily: 'inherit', fontSize: '1.1em', fontWeight: 700 }}>{watchedName || 'Business Name'}</Typography>
                      {watchedReceipt.tagline && <Typography sx={{ fontFamily: 'inherit', fontSize: '0.9em' }}>{watchedReceipt.tagline}</Typography>}
                      {watchedReceipt.show_phone !== false && watchedPhone && <Typography sx={{ fontFamily: 'inherit' }}>Tel: {watchedPhone}</Typography>}
                      {watchedReceipt.show_address !== false && watchedAddress && <Typography sx={{ fontFamily: 'inherit' }}>{watchedAddress}</Typography>}
                      <Typography sx={{ fontFamily: 'inherit' }}>{'─'.repeat(30)}</Typography>
                      <Typography sx={{ fontFamily: 'inherit' }}>Invoice: INV-000001</Typography>
                      <Typography sx={{ fontFamily: 'inherit' }}>{new Date().toLocaleDateString('en-PK')}</Typography>
                      {watchedReceipt.show_cashier !== false && <Typography sx={{ fontFamily: 'inherit' }}>Cashier: Demo User</Typography>}
                      <Typography sx={{ fontFamily: 'inherit' }}>{'─'.repeat(30)}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontFamily: 'inherit' }}>Product A</Typography>
                      <Typography sx={{ fontFamily: 'inherit' }}>  1 x Rs.500{'  '}{'  '}{'  '}{'  '}{'  '}Rs.500</Typography>
                      <Typography sx={{ fontFamily: 'inherit' }}>Product B</Typography>
                      <Typography sx={{ fontFamily: 'inherit' }}>  2 x Rs.150{'  '}{'  '}{'  '}{'  '}{'  '}Rs.300</Typography>
                    </Box>
                    <Typography sx={{ fontFamily: 'inherit' }}>{'═'.repeat(30)}</Typography>
                    <Typography sx={{ fontFamily: 'inherit', fontWeight: 700 }}>TOTAL{'  '}{'  '}{'  '}{'  '}{'  '}{'  '}{'  '}{'  '}Rs.800</Typography>
                    {watchedReceipt.show_loyalty !== false && <Typography sx={{ fontFamily: 'inherit' }}>Points: 8 earned (total: 48)</Typography>}
                    <Typography sx={{ fontFamily: 'inherit' }}>{'═'.repeat(30)}</Typography>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontFamily: 'inherit' }}>{watchedFooter || 'Thank you for your business!'}</Typography>
                      {watchedReceipt.footer_line2 && <Typography sx={{ fontFamily: 'inherit' }}>{watchedReceipt.footer_line2}</Typography>}
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Receipt Settings'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          {/* Appearance */}
          <TabPanel value={tab} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 400 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Theme Mode</Typography>
                <FormControlLabel
                  control={<Switch checked={darkMode} onChange={() => dispatch(toggleDarkMode())} />}
                  label={darkMode ? 'Dark Mode' : 'Light Mode'}
                />
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>Color Scheme</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444'].map(color => (
                    <Box key={color} sx={{ width: 36, height: 36, bgcolor: color, borderRadius: 2, cursor: 'pointer', border: '3px solid', borderColor: color === '#6366f1' ? 'primary.main' : 'transparent', '&:hover': { opacity: 0.8 } }} />
                  ))}
                </Box>
              </Box>
            </Box>
          </TabPanel>

          {/* Language */}
          <TabPanel value={tab} index={3}>
            <Box sx={{ maxWidth: 500 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>Select Interface Language</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Card
                  onClick={() => handleLang('en')}
                  sx={{ flex: 1, cursor: 'pointer', border: '2px solid', borderColor: language === 'en' ? 'primary.main' : 'divider', '&:hover': { borderColor: 'primary.light' } }}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4">🇬🇧</Typography>
                    <Typography fontWeight={600} mt={1}>English</Typography>
                    <Typography variant="caption" color="text.secondary">Left to Right (LTR)</Typography>
                  </CardContent>
                </Card>
                <Card
                  onClick={() => handleLang('ur')}
                  sx={{ flex: 1, cursor: 'pointer', border: '2px solid', borderColor: language === 'ur' ? 'primary.main' : 'divider', '&:hover': { borderColor: 'primary.light' } }}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4">🇵🇰</Typography>
                    <Typography fontWeight={600} mt={1}>اردو</Typography>
                    <Typography variant="caption" color="text.secondary">Right to Left (RTL)</Typography>
                  </CardContent>
                </Card>
              </Box>
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                Switching to Urdu enables RTL layout and Nastaliq font automatically.
              </Alert>
            </Box>
          </TabPanel>

          {/* Notifications */}
          <TabPanel value={tab} index={4}>
            <Box sx={{ maxWidth: 500, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[
                { label: 'Low Stock Alerts', defaultOn: true },
                { label: 'Expiry Date Alerts (Medical)', defaultOn: true },
                { label: 'Pending Payment Reminders', defaultOn: true },
                { label: 'Daily Sales Summary', defaultOn: false },
                { label: 'WhatsApp Notifications', defaultOn: false },
              ].map(item => (
                <FormControlLabel
                  key={item.label}
                  control={<Switch defaultChecked={item.defaultOn} />}
                  label={item.label}
                  sx={{ justifyContent: 'space-between', ml: 0, flexDirection: 'row-reverse' }}
                />
              ))}
              <Button variant="contained" startIcon={<Save />} onClick={() => dispatch(showSnackbar({ message: 'Notification settings saved', severity: 'success' }))} sx={{ mt: 1, alignSelf: 'flex-start' }}>
                Save
              </Button>
            </Box>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
}
